import { FunctionComponent, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Tab,
  TextField,
  Typography,
} from '@mui/material'
import PageWrapper from 'hoc/PageWrapper'
import { useTranslation } from 'react-i18next'
import { TablePagination } from 'components/TablePagination'
import { useQueryState } from 'react-router-use-location-state'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { OrganizationTable } from './organizations-table'
import { useOrganizationsQuery } from 'services/organizations'
import { OrganizationType } from 'types/organization'
import { useBoolean } from 'hooks/useBoolean'
import { CreateUserRequestOrganization } from './create-organization'
import { TabContext, TabList } from '@mui/lab'
import { useSearch } from 'hooks/useSearch'
import SearchIcon from '@mui/icons-material/Search'

export const Organizations: FunctionComponent = () => {
  const [tab, setTab] = useState<OrganizationType>(OrganizationType.CLIENT)
  const [searchNameQuery, setSearchNameQuery] = useState('')

  const { query: debouncedSearchNameQuery } = useSearch(searchNameQuery)
  const dialogCreateOrganization = useBoolean(false)
  const { t } = useTranslation()

  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10

  const orgTabs = useMemo(() => {
    return [
      {
        value: OrganizationType.CLIENT,
        label: t(OrganizationType.CLIENT),
      },
      {
        value: OrganizationType.TENDER_AUTHORITY,
        label: t(OrganizationType.TENDER_AUTHORITY),
      },
    ]
  }, [t])

  const args = {
    type: tab,
    name: debouncedSearchNameQuery,
    email: '',
    taxId: '',
    page,
    size: rowsPerPage,
  } as const

  const { data, isLoading, isError } = useOrganizationsQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  if (isLoading) return <Loading />

  if (isError) return <Error />

  if (data === undefined) {
    return <></>
  }

  return (
    <PageWrapper>
      {dialogCreateOrganization.isTrue && (
        <CreateUserRequestOrganization onClose={dialogCreateOrganization.setFalse} />
      )}
      <Box display="flex" justifyContent="space-between">
        <Typography fontWeight="bold" variant="h6">
          {t('organizations')}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          onClick={dialogCreateOrganization.setTrue}
        >
          {t('create')}
        </Button>
      </Box>

      <TabContext value={tab}>
        <TabList
          onChange={(_event, newValue) => {
            setTab(newValue)
            setPage(0)
          }}
          sx={{
            '&& .MuiTabs-flexContainer': {
              justifyContent: 'center',
            },
          }}
        >
          {orgTabs.map(({ value, label }) => (
            <Tab
              sx={{ background: '#FFF' }}
              key={value}
              label={label}
              value={value}
            />
          ))}
        </TabList>
        <Divider sx={{ my: 1.5 }} />
      </TabContext>

      <TextField
        label={t('search-based-on-name')}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        value={searchNameQuery}
        onChange={(event) => setSearchNameQuery(event.target.value)}
        sx={{ mb: 1.5 }}
      />

      {data && data.items.length > 0 && <OrganizationTable data={data.items} />}

      {data.info.totalRows !== 0 && (
        <TablePagination
          total={data.info.totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={setPage}
        />
      )}
    </PageWrapper>
  )
}
